import { useEffect, useMemo, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "convex/react";
import { ActionModal } from "../components/ActionModal";
import { Avatar } from "../components/ui";
import { CooperationRequestModal } from "../components/CooperationRequestModal";
import { convexApi } from "../convexApi";
import { cooperationRequestMutationArgs, createCooperationRequest, labelForRequestType, validateCooperationRequestDraft } from "../lib/cooperation";
import { canViewAffiliationGroup, filterMembers, getFirmPracticeAreas, getUserPracticeAreas } from "../lib/domain";
import type { CooperationRequest, CooperationRequestType, Firm, LexData, MembershipCategory } from "../lib/types";

type MembersPersistence = {
  createRequest?: (request: CooperationRequest) => Promise<unknown>;
  addMessage?: (cooperationRequestId: string, body: string) => Promise<unknown>;
};

// Cooperation interests that imply the firm takes on cross-border work.
const crossBorderInterests: CooperationRequestType[] = ["local_counsel", "co_counsel", "jurisdiction_assistance", "matter_referral"];

function offersCrossBorder(firm: Firm) {
  return (firm.cooperationInterests ?? []).some((interest) => crossBorderInterests.includes(interest));
}

const AREA_PALETTE = ["#7A1C2C", "#1c3a5e", "#2f7d57", "#9a7416", "#5b3a7c", "#0f766e", "#b45309", "#475569"];
function paletteIndex(key: string, mod: number) {
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) hash = (hash * 31 + key.charCodeAt(index)) % 100000;
  return hash % mod;
}
const areaColor = (id: string) => AREA_PALETTE[paletteIndex(id, AREA_PALETTE.length)];
const REGION_CODES: Record<string, string> = { luxembourg: "LU", belgium: "BE", switzerland: "CH", france: "FR", germany: "DE", italy: "IT", "united kingdom": "UK", netherlands: "NL", spain: "ES", "european union": "EU", eu: "EU" };
const regionCode = (jurisdiction: string) => REGION_CODES[jurisdiction.toLowerCase().trim()] ?? jurisdiction.slice(0, 2).toUpperCase();
const initialsOf = (name: string) => name.split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase();
function practiceFit(shared: number, total: number): { label: string; bar: number; color: string } | null {
  if (total === 0) return null;
  const ratio = shared / total;
  if (ratio >= 0.5) return { label: "Strong fit", bar: 0.95, color: "#2f7d57" };
  if (ratio >= 0.25) return { label: "Good fit", bar: 0.68, color: "#2f7d57" };
  if (ratio > 0) return { label: "Some overlap", bar: 0.42, color: "#9a7416" };
  return { label: "Different focus", bar: 0.12, color: "var(--muted)" };
}

function externalHref(url: string) {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}
function socialHref(kind: "telegram" | "whatsapp" | "linkedin" | "github", value: string) {
  const raw = value.trim();
  if (/^https?:\/\//i.test(raw)) return raw;
  if (kind === "telegram") return raw.includes("t.me/") ? `https://${raw}` : `https://t.me/${raw.replace(/^@/, "")}`;
  if (kind === "whatsapp") {
    if (raw.includes("wa.me")) return `https://${raw}`;
    const digits = raw.replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : externalHref(raw);
  }
  return externalHref(raw);
}

type DirMember = LexData["users"][number];
const profileLinkStyle = { color: "var(--burg)", fontWeight: 600 as const, textDecoration: "underline" };

function ProfileModal({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-navy/45 p-0 backdrop-blur-[2px] sm:place-items-center sm:p-4" role="dialog" aria-modal="true">
      <button type="button" aria-label="Close" className="absolute inset-0 h-full w-full cursor-default" onClick={onClose} />
      <div className="relative max-h-[92vh] w-full overflow-auto rounded-t-2xl border border-line bg-elevated p-5 shadow-soft sm:max-w-xl sm:rounded-2xl">
        <button type="button" className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-lg border border-line hover:bg-hover" onClick={onClose} aria-label="Close"><X className="h-4 w-4" /></button>
        {children}
      </div>
    </div>
  );
}

function ProfileField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 10, fontSize: 14, alignItems: "baseline" }}>
      <span className="mono" style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", width: 92, flexShrink: 0 }}>{label}</span>
      <span style={{ color: "var(--ink)", minWidth: 0 }}>{children}</span>
    </div>
  );
}

function FirmProfileModal({ firm, data, onClose, onOpenMember, onMessage, onCooperate }: { firm: Firm; data: LexData; onClose: () => void; onOpenMember: (member: DirMember) => void; onMessage: () => void; onCooperate: (type: CooperationRequestType) => void }) {
  const areas = getFirmPracticeAreas(firm.id, data.firmPracticeAreas, data.practiceAreas);
  const affiliation = data.affiliationGroups.find((group) => group.id === firm.affiliationGroupId);
  const members = data.users.filter((user) => user.firmId === firm.id && user.active !== false && user.memberCategory !== "observer");
  const interests = firm.cooperationInterests ?? [];
  return (
    <ProfileModal onClose={onClose}>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", paddingRight: 40 }}>
        {firm.logoUrl ? <img src={firm.logoUrl} alt={firm.name} style={{ width: 56, height: 56, borderRadius: 12, objectFit: "cover", flexShrink: 0 }} /> : <div style={{ width: 56, height: 56, borderRadius: 12, background: areaColor(firm.id), color: "#fff", display: "grid", placeItems: "center", fontSize: 19, fontWeight: 600, flexShrink: 0 }}>{initialsOf(firm.name)}</div>}
        <div style={{ minWidth: 0 }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.13em", textTransform: "uppercase", color: "var(--muted)" }}>Firm profile</div>
          <h2 style={{ fontSize: 22, fontWeight: 600, color: "var(--ink)", margin: "2px 0 0" }}>{firm.name}</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 5 }}>
            <span className="mono" style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", color: "var(--burg)", border: "1px solid #e6cdd2", background: "#f7ebec", borderRadius: 6, padding: "2px 7px" }}>{firm.membershipCategory}</span>
            <span className="mono" style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ink2)", background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 5, padding: "1px 6px" }}>{regionCode(firm.jurisdiction)}</span>
            <span style={{ fontSize: 14, color: "var(--ink2)" }}>{firm.jurisdiction}</span>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 9, marginTop: 16 }}>
        {firm.websiteUrl ? <ProfileField label="Website"><a href={externalHref(firm.websiteUrl)} target="_blank" rel="noreferrer" style={profileLinkStyle}>{firm.websiteUrl}</a></ProfileField> : null}
        {firm.linkedin ? <ProfileField label="LinkedIn"><a href={externalHref(firm.linkedin)} target="_blank" rel="noreferrer" style={profileLinkStyle}>{firm.linkedin}</a></ProfileField> : null}
        {firm.contactEmail ? <ProfileField label="Email"><a href={`mailto:${firm.contactEmail}`} style={profileLinkStyle}>{firm.contactEmail}</a></ProfileField> : null}
        {firm.telephone ? <ProfileField label="Phone"><a href={`tel:${firm.telephone.replace(/\s+/g, "")}`} style={profileLinkStyle}>{firm.telephone}</a></ProfileField> : null}
        {firm.address ? <ProfileField label="Address">{firm.address}</ProfileField> : null}
        {firm.languages?.length ? <ProfileField label="Languages">{firm.languages.join(" · ")}</ProfileField> : null}
        {firm.responseTimeIndicator ? <ProfileField label="Response">{firm.responseTimeIndicator}</ProfileField> : null}
        {affiliation && canViewAffiliationGroup(data.viewer, firm) ? <ProfileField label="Network">{affiliation.name}</ProfileField> : null}
      </div>

      {interests.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 7 }}>Open to</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {interests.map((interest) => <span key={interest} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, color: "#2f7d57", background: "#e4f1e9", border: "1px solid #bfe0cd", borderRadius: 999, padding: "4px 10px" }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>{labelForRequestType(interest)}</span>)}
          </div>
        </div>
      ) : null}

      {areas.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 7 }}>Practice areas</div>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {areas.map((area) => <span key={area.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink2)", border: "1px solid var(--line)", borderRadius: 999, padding: "3px 9px" }}><span style={{ width: 7, height: 7, borderRadius: 999, background: areaColor(area.id) }} />{area.name}</span>)}
          </div>
        </div>
      ) : null}

      {members.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 }}>Members</div>
          <div style={{ display: "grid", gap: 9 }}>
            {members.map((member) => {
              const repLabel = member.representativeType === "principal" ? "Principal rep" : member.representativeType === "alternate" ? "Alternate rep" : null;
              return (
                <div key={member.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Avatar name={member.name} imageUrl={member.avatarUrl} size="sm" />
                  <button type="button" className="dir-link" onClick={() => onOpenMember(member)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontWeight: 600, color: "var(--ink)", fontSize: 14 }}>{member.name}</button>
                  {repLabel ? <span style={{ fontSize: 11.5, color: "var(--ink2)", background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 6, padding: "1px 7px" }}>{repLabel}</span> : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
        <button type="button" className="btn-sm btn-primary" style={{ height: 38 }} onClick={onMessage}>Send a message</button>
        <button type="button" className="btn-sm" style={{ height: 38 }} onClick={() => onCooperate("ask_view")}>Ask their view</button>
        <button type="button" className="btn-sm" style={{ height: 38 }} onClick={() => onCooperate("local_counsel")}>Request expertise</button>
      </div>
    </ProfileModal>
  );
}

function MemberProfileModal({ member, firm, data, onClose, onOpenFirm }: { member: DirMember; firm: Firm; data: LexData; onClose: () => void; onOpenFirm: () => void }) {
  const areas = getUserPracticeAreas(member.id, data.userPracticeAreas, data.practiceAreas);
  const repLabel = member.representativeType === "principal" ? "Principal representative" : member.representativeType === "alternate" ? "Alternate representative" : null;
  return (
    <ProfileModal onClose={onClose}>
      <div style={{ display: "flex", gap: 14, alignItems: "center", paddingRight: 40 }}>
        <Avatar name={member.name} imageUrl={member.avatarUrl} size="lg" />
        <div style={{ minWidth: 0 }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.13em", textTransform: "uppercase", color: "var(--muted)" }}>Member profile</div>
          <h2 style={{ fontSize: 22, fontWeight: 600, color: "var(--ink)", margin: "2px 0 0" }}>{member.name}</h2>
          {repLabel ? <div style={{ fontSize: 13.5, color: "var(--ink2)", marginTop: 3 }}>{repLabel}</div> : null}
        </div>
      </div>
      <div style={{ display: "grid", gap: 9, marginTop: 16 }}>
        <ProfileField label="Firm"><button type="button" className="dir-link" onClick={onOpenFirm} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", ...profileLinkStyle }}>{firm.name}</button> · {firm.jurisdiction}</ProfileField>
        {member.email ? <ProfileField label="Email"><a href={`mailto:${member.email}`} style={profileLinkStyle}>{member.email}</a></ProfileField> : null}
        {member.mobile ? <ProfileField label="Mobile"><a href={`tel:${member.mobile.replace(/\s+/g, "")}`} style={profileLinkStyle}>{member.mobile}</a></ProfileField> : null}
        {member.telegram ? <ProfileField label="Telegram"><a href={socialHref("telegram", member.telegram)} target="_blank" rel="noreferrer" style={profileLinkStyle}>{member.telegram}</a></ProfileField> : null}
        {member.whatsapp ? <ProfileField label="WhatsApp"><a href={socialHref("whatsapp", member.whatsapp)} target="_blank" rel="noreferrer" style={profileLinkStyle}>{member.whatsapp}</a></ProfileField> : null}
        {member.linkedin ? <ProfileField label="LinkedIn"><a href={socialHref("linkedin", member.linkedin)} target="_blank" rel="noreferrer" style={profileLinkStyle}>{member.linkedin}</a></ProfileField> : null}
        {member.github ? <ProfileField label="GitHub"><a href={socialHref("github", member.github)} target="_blank" rel="noreferrer" style={profileLinkStyle}>{member.github}</a></ProfileField> : null}
        {member.website ? <ProfileField label="Website"><a href={externalHref(member.website)} target="_blank" rel="noreferrer" style={profileLinkStyle}>{member.website}</a></ProfileField> : null}
        {firm.languages?.length ? <ProfileField label="Languages">{firm.languages.join(" · ")}</ProfileField> : null}
      </div>
      {areas.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 7 }}>Practice areas</div>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {areas.map((area) => <span key={area.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink2)", border: "1px solid var(--line)", borderRadius: 999, padding: "3px 9px" }}><span style={{ width: 7, height: 7, borderRadius: 999, background: areaColor(area.id) }} />{area.name}</span>)}
          </div>
        </div>
      ) : null}
    </ProfileModal>
  );
}

export function MembersPage({ data, mode = "demo" }: { data: LexData; mode?: "convex" | "demo" }) {
  if (mode === "convex") return <LiveMembersPage data={data} />;
  return <MembersPageContent data={data} />;
}

function LiveMembersPage({ data }: { data: LexData }) {
  const createRequestMutation = useMutation(convexApi.cooperation.createRequest);
  const addMessageMutation = useMutation(convexApi.cooperation.addMessage);
  const persistence: MembersPersistence = {
    createRequest: (request) => createRequestMutation(cooperationRequestMutationArgs(request) as never),
    addMessage: (cooperationRequestId, body) => addMessageMutation({ cooperationRequestId: cooperationRequestId as never, messageBody: body, containsConfidentialDetails: false }),
  };
  return <MembersPageContent data={data} persistence={persistence} />;
}

function MembersPageContent({ data, persistence }: { data: LexData; persistence?: MembersPersistence }) {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [practiceAreaId, setPracticeAreaId] = useState("");
  const [acceptsOnly, setAcceptsOnly] = useState(true);
  const [membershipCategory, setMembershipCategory] = useState<MembershipCategory | "all">("all");
  const [language, setLanguage] = useState("");
  const [crossBorder, setCrossBorder] = useState(false);
  const [action, setAction] = useState<{ kind: "message"; firm: Firm } | null>(null);
  const [messageSubject, setMessageSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [messageError, setMessageError] = useState<string | null>(null);
  const [messageSending, setMessageSending] = useState(false);
  const [cooperationDraft, setCooperationDraft] = useState<{ firm: Firm; type: CooperationRequestType } | null>(null);
  const [createdRequest, setCreatedRequest] = useState<CooperationRequest | null>(null);
  const [profileFirm, setProfileFirm] = useState<Firm | null>(null);
  const [profileMember, setProfileMember] = useState<{ member: DirMember; firm: Firm } | null>(null);

  const activeFirmIds = new Set(data.firms.filter((firm) => firm.status === "active").map((firm) => firm.id));
  const usedPracticeIds = new Set(data.firmPracticeAreas.filter((link) => activeFirmIds.has(link.firmId)).map((link) => link.practiceAreaId));
  const attributedPracticeAreas = data.practiceAreas.filter((area) => area.active && usedPracticeIds.has(area.id));
  const allJurisdictions = [...new Set(data.firms.filter((firm) => firm.status === "active").map((firm) => firm.jurisdiction))].sort();
  const requestedCooperationFirm = searchParams.get("cooperate");
  const requestedMessageFirm = searchParams.get("message");

  useEffect(() => {
    if (!requestedCooperationFirm) return;
    const firm = data.firms.find((item) => item.id === requestedCooperationFirm);
    if (firm) setCooperationDraft({ firm, type: "local_counsel" });
  }, [data.firms, requestedCooperationFirm]);

  useEffect(() => {
    if (!requestedMessageFirm) return;
    const firm = data.firms.find((item) => item.id === requestedMessageFirm);
    if (firm) setAction({ kind: "message", firm });
  }, [data.firms, requestedMessageFirm]);

  const firms = useMemo(() => {
    const base = filterMembers(data.firms, data.firmPracticeAreas, {
      search: "",
      jurisdictions: jurisdiction ? [jurisdiction] : [],
      practiceAreaIds: practiceAreaId ? [practiceAreaId] : [],
      matchAllPracticeAreas: false,
      statuses: ["active", "pending"],
    });
    const query = search.trim().toLowerCase();
    return base.filter((firm) => {
      if (acceptsOnly && firm.acceptsCooperationRequests === false) return false;
      if (membershipCategory !== "all" && firm.membershipCategory !== membershipCategory) return false;
      if (language && !(firm.languages ?? []).includes(language)) return false;
      if (crossBorder && !offersCrossBorder(firm)) return false;
      if (query) {
        const areaNames = getFirmPracticeAreas(firm.id, data.firmPracticeAreas, data.practiceAreas).map((area) => area.name).join(" ");
        if (!`${firm.name} ${firm.jurisdiction} ${areaNames}`.toLowerCase().includes(query)) return false;
      }
      return true;
    });
  }, [acceptsOnly, crossBorder, data.firmPracticeAreas, data.firms, data.practiceAreas, jurisdiction, language, membershipCategory, practiceAreaId, search]);

  // The viewer's practice areas (their own, or their firm's) — used for the qualitative "practice fit".
  const viewerAreaIds = useMemo(() => {
    const own = getUserPracticeAreas(data.viewer.id, data.userPracticeAreas, data.practiceAreas).map((area) => area.id);
    if (own.length > 0) return new Set(own);
    if (data.viewerFirm) return new Set(getFirmPracticeAreas(data.viewerFirm.id, data.firmPracticeAreas, data.practiceAreas).map((area) => area.id));
    return new Set<string>();
  }, [data.viewer.id, data.viewerFirm, data.userPracticeAreas, data.firmPracticeAreas, data.practiceAreas]);

  // Firms grouped with their members (principal rep, alternate rep, then any other active member).
  type FirmGroup = { firm: Firm; members: (typeof data.users)[number][] };
  const firmGroups = useMemo<FirmGroup[]>(() => {
    const rank = (user: (typeof data.users)[number]) => (user.representativeType === "principal" ? 0 : user.representativeType === "alternate" ? 1 : 2);
    return firms.map((firm) => ({
      firm,
      members: data.users
        .filter((user) => user.firmId === firm.id && user.active !== false && user.memberCategory !== "observer")
        .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name)),
    }));
  }, [firms, data.users]);
  const totalMembers = useMemo(() => firmGroups.reduce((sum, group) => sum + group.members.length, 0), [firmGroups]);

  const sendMemberMessage = async () => {
    if (!action || messageSending) return;
    const title = messageSubject.trim();
    const body = messageBody.trim();
    if (!title || !body) {
      setMessageError("Add a subject and a short message.");
      return;
    }
    const draft = {
      requestType: "ask_view" as const,
      title,
      shortSummary: body,
      jurisdictionTags: [action.firm.jurisdiction],
      practiceAreaIds: data.practiceAreas[0]?.id ? [data.practiceAreas[0].id] : [],
      urgency: "normal" as const,
      confidentialityLevel: "requester_and_target_only" as const,
      requesterFirmId: data.viewerFirm?.id ?? "",
      requesterUserId: data.viewer.id,
      targetFirmId: action.firm.id,
      targetUserId: null,
      status: "sent" as const,
      conflictCheckRequired: false,
      clientDisclosed: false,
      confidentialDetailsAdded: false,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    const validation = validateCooperationRequestDraft(draft);
    if (validation) {
      setMessageError(validation);
      return;
    }
    setMessageSending(true);
    setMessageError(null);
    try {
      const request = createCooperationRequest({ ...draft, relatedMemberId: null, allowFirmRouting: true, deadline: null });
      const persistedId = await persistence?.createRequest?.(request);
      await persistence?.addMessage?.(typeof persistedId === "string" ? persistedId : request.id, body);
      setCreatedRequest(request);
      setAction(null);
      setMessageSubject("");
      setMessageBody("");
    } catch (error) {
      setMessageError(error instanceof Error ? error.message : "The message could not be sent.");
    } finally {
      setMessageSending(false);
    }
  };

  return (
    <>
      <div className="phead">
        <div className="eyebrow">Members directory</div>
        <h1 className="h1 serif">Find the right firm</h1>
        <p className="lead">Search the network by jurisdiction, practice area and expertise-exchange interests. Member-to-member contact runs through structured requests — never client names first.</p>
      </div>

      {createdRequest ? (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 14, fontSize: 16, color: "var(--ink2)" }}>
          Created expertise exchange request: <strong style={{ color: "var(--ink)" }}>{createdRequest.title}</strong>. It is visible in Expertise exchange.
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <div className="searchbtn" style={{ flex: "1 1 320px", minWidth: 240, background: "#fff", color: "var(--ink)", marginRight: 0 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by firm, jurisdiction or practice area…" style={{ border: "none", outline: "none", background: "transparent", flex: 1, fontSize: 16, color: "var(--ink)", fontFamily: "inherit", minWidth: 0 }} />
        </div>
        <select className="select" value={jurisdiction} onChange={(event) => setJurisdiction(event.target.value)}>
          <option value="">All jurisdictions</option>
          {allJurisdictions.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
        <select className="select" value={practiceAreaId} onChange={(event) => setPracticeAreaId(event.target.value)}>
          <option value="">All practice areas</option>
          {attributedPracticeAreas.map((area) => <option key={area.id} value={area.id}>{area.name}</option>)}
        </select>
        <select className="select" value={membershipCategory} onChange={(event) => setMembershipCategory(event.target.value as MembershipCategory | "all")}>
          <option value="all">All tiers</option>
          <option value="Full Member">Full Member</option>
          <option value="Associate Member">Associate Member</option>
          <option value="Expert Contributor">Expert Contributor</option>
        </select>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
        {([
          { label: "Accepts exchange requests", active: acceptsOnly, onClick: () => setAcceptsOnly((value) => !value) },
          { label: "English-speaking", active: language === "English", onClick: () => setLanguage((value) => (value === "English" ? "" : "English")) },
          { label: "Cross-border", active: crossBorder, onClick: () => setCrossBorder((value) => !value) },
        ]).map((chip) => (
          <button key={chip.label} type="button" onClick={chip.onClick} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 15px", borderRadius: 999, border: `1px solid ${chip.active ? "var(--burg)" : "var(--line)"}`, background: chip.active ? "var(--burg)" : "#fff", color: chip.active ? "#fff" : "var(--ink)", fontSize: 14, fontWeight: chip.active ? 600 : 400, cursor: "pointer" }}>
            <span style={{ width: 7, height: 7, borderRadius: 999, background: chip.active ? "#fff" : "var(--muted)" }} />
            {chip.label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontSize: 15, color: "var(--ink2)" }}>
          <strong style={{ color: "var(--burg)" }}>{firmGroups.length}</strong> {firmGroups.length === 1 ? "firm" : "firms"} · <strong style={{ color: "var(--burg)" }}>{totalMembers}</strong> {totalMembers === 1 ? "member" : "members"}
        </span>
        <span className="row-meta" style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="6" x2="20" y2="6" /><line x1="7" y1="12" x2="17" y2="12" /><line x1="10" y1="18" x2="14" y2="18" /></svg>
          Sorted by relevance to your practice
        </span>
      </div>

      {firmGroups.length > 0 ? firmGroups.map(({ firm, members }) => {
        const firmAreas = getFirmPracticeAreas(firm.id, data.firmPracticeAreas, data.practiceAreas);
        const affiliation = data.affiliationGroups.find((group) => group.id === firm.affiliationGroupId);
        const fit = practiceFit(firmAreas.filter((area) => viewerAreaIds.has(area.id)).length, viewerAreaIds.size);
        const interests = firm.cooperationInterests ?? [];
        return (
          <div className="fcard" key={firm.id}>
            <div className="fcard-main">
              <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
                <button type="button" aria-label={`${firm.name} profile`} onClick={() => setProfileFirm(firm)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", flexShrink: 0, borderRadius: 12 }}>
                  {firm.logoUrl ? (
                    <img src={firm.logoUrl} alt={firm.name} style={{ width: 54, height: 54, borderRadius: 12, objectFit: "cover", display: "block" }} />
                  ) : (
                    <div style={{ width: 54, height: 54, borderRadius: 12, background: areaColor(firm.id), color: "#fff", display: "grid", placeItems: "center", fontSize: 18, fontWeight: 600 }}>{initialsOf(firm.name)}</div>
                  )}
                </button>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <button type="button" className="dir-link" onClick={() => setProfileFirm(firm)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 19, fontWeight: 600, color: "var(--ink)", textAlign: "left" }}>{firm.name}</button>
                    <span className="mono" style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--burg)", border: "1px solid #e6cdd2", background: "#f7ebec", borderRadius: 6, padding: "2px 7px" }}>{firm.membershipCategory}</span>
                    {firm.status === "pending" ? <span className="mono" style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#9a7416", border: "1px solid #ecd9a6", background: "#fbf0d6", borderRadius: 6, padding: "2px 7px" }}>Prospective</span> : null}
                    <span className="mono" style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ink2)", background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 5, padding: "1px 6px" }}>{regionCode(firm.jurisdiction)}</span>
                    <span style={{ fontSize: 14, color: "var(--ink2)" }}>{firm.jurisdiction}</span>
                    {affiliation && canViewAffiliationGroup(data.viewer, firm) ? <span className="tag">{affiliation.name}</span> : null}
                  </div>
                  {firm.languages?.length ? (
                    <div style={{ marginTop: 7, fontSize: 13.5, color: "var(--ink2)" }}>
                      <span className="mono" style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginRight: 8 }}>Languages</span>
                      {firm.languages.join(" · ")}
                    </div>
                  ) : null}
                </div>
              </div>

              {interests.length > 0 ? (
                <div style={{ marginTop: 14, paddingTop: 13, borderTop: "1px dashed var(--line)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span className="mono" style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)" }}>Open to</span>
                  {interests.map((interest) => (
                    <span key={interest} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, color: "#2f7d57", background: "#e4f1e9", border: "1px solid #bfe0cd", borderRadius: 999, padding: "4px 10px" }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                      {labelForRequestType(interest)}
                    </span>
                  ))}
                </div>
              ) : null}

              <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
                {members.length > 0 ? members.map((member) => {
                  const memberAreas = getUserPracticeAreas(member.id, data.userPracticeAreas, data.practiceAreas);
                  const repLabel = member.representativeType === "principal" ? "Principal rep" : member.representativeType === "alternate" ? "Alternate rep" : null;
                  return (
                    <div key={member.id} style={{ display: "flex", alignItems: "flex-start", gap: 11 }}>
                      <Avatar name={member.name} imageUrl={member.avatarUrl} size="sm" />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                          <button type="button" className="dir-link" onClick={() => setProfileMember({ member, firm })} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontWeight: 600, color: "var(--ink)", fontSize: 14.5, textAlign: "left" }}>{member.name}</button>
                          {repLabel ? <span style={{ fontSize: 11.5, color: "var(--ink2)", background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 6, padding: "1px 7px" }}>{repLabel}</span> : null}
                        </div>
                        {memberAreas.length > 0 ? (
                          <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 6 }}>
                            {memberAreas.slice(0, 4).map((area) => (
                              <span key={area.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink2)", border: "1px solid var(--line)", borderRadius: 999, padding: "3px 9px" }}>
                                <span style={{ width: 7, height: 7, borderRadius: 999, background: areaColor(area.id) }} />
                                {area.name}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                }) : (
                  <span className="row-meta" style={{ margin: 0 }}>No active members listed yet.</span>
                )}
              </div>
            </div>

            <div className="fcard-side">
              {fit ? (
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: fit.color }}>{fit.label}</div>
                  <div style={{ height: 5, borderRadius: 999, background: "var(--line)", marginTop: 7, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${Math.round(fit.bar * 100)}%`, background: fit.color, borderRadius: 999 }} />
                  </div>
                  <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginTop: 7 }}>Practice fit</div>
                </div>
              ) : <div />}
              <div style={{ display: "grid", gap: 8 }}>
                <button type="button" className="btn-sm btn-primary" style={{ justifyContent: "center", height: 38 }} onClick={() => setAction({ kind: "message", firm })}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a8 8 0 0 1-11.3 7.3L3 21l1.7-6.7A8 8 0 1 1 21 12z" /></svg>
                  Send a message
                </button>
                <button type="button" className="btn-sm" style={{ justifyContent: "center", height: 38 }} onClick={() => setCooperationDraft({ firm, type: "ask_view" })}>Ask their view</button>
                <button type="button" className="btn-sm" style={{ justifyContent: "center", height: 38 }} onClick={() => setCooperationDraft({ firm, type: "local_counsel" })}>Request expertise</button>
              </div>
            </div>
          </div>
        );
      }) : (
        <div className="card"><div className="rec"><div className="row-meta" style={{ margin: 0 }}>No firms match these filters.</div></div></div>
      )}

      {action ? (
        <ActionModal
          title="Send a message"
          description={`${action.firm.name} · ${action.firm.jurisdiction}`}
          confirmLabel={messageSending ? "Sending..." : "Send message"}
          onClose={() => setAction(null)}
          onConfirm={messageSending ? undefined : sendMemberMessage}
        >
          <div className="grid gap-3">
            {messageError ? <div className="rounded-lg border border-danger/30 bg-[#FFF7F4] px-3 py-2 text-sm text-danger">{messageError}</div> : null}
            <label>
              <span className="mb-1 block text-xs font-medium text-muted">Subject</span>
              <input className="min-h-11 w-full rounded-lg border border-line bg-surface px-3" value={messageSubject} onChange={(event) => setMessageSubject(event.target.value)} />
            </label>
            <label>
              <span className="mb-1 block text-xs font-medium text-muted">Message</span>
              <textarea className="min-h-32 w-full rounded-lg border border-line bg-surface px-3 py-2" placeholder="Write a concise member-to-member note. Do not include client names or privileged details." value={messageBody} onChange={(event) => setMessageBody(event.target.value)} />
            </label>
          </div>
        </ActionModal>
      ) : null}
      {cooperationDraft ? (
        <CooperationRequestModal
          data={data}
          defaultTargetFirm={cooperationDraft.firm}
          defaultRequestType={cooperationDraft.type}
          onClose={() => setCooperationDraft(null)}
          onCreate={(request) => {
            void persistence?.createRequest?.(request);
            setCreatedRequest(request);
            setCooperationDraft(null);
          }}
        />
      ) : null}

      {profileFirm ? (
        <FirmProfileModal
          firm={profileFirm}
          data={data}
          onClose={() => setProfileFirm(null)}
          onOpenMember={(member) => { setProfileMember({ member, firm: profileFirm }); setProfileFirm(null); }}
          onMessage={() => { const firm = profileFirm; setProfileFirm(null); setAction({ kind: "message", firm }); }}
          onCooperate={(type) => { const firm = profileFirm; setProfileFirm(null); setCooperationDraft({ firm, type }); }}
        />
      ) : null}

      {profileMember ? (
        <MemberProfileModal
          member={profileMember.member}
          firm={profileMember.firm}
          data={data}
          onClose={() => setProfileMember(null)}
          onOpenFirm={() => { setProfileFirm(profileMember.firm); setProfileMember(null); }}
        />
      ) : null}
    </>
  );
}
